// 再検索は facets.js が form の `input` を 800ms debounce で拾う仕組みに乗る。プログラムでの
// value 変更では `input` が発火しないので、bubbling する input イベントを明示的に投げる。
// AJAX 再描画で中身が差し替わり connectedCallback が再走するので、状態は要素内に閉じる。
(function () {
  'use strict';

  // 税込計算の端数処理 → 切り捨て除算へ足し込むオフセット（snippets/maruni-tax-included-amount.liquid と対）
  const TAX_ROUND_OFFSET = { floor: 0, round: 50, ceil: 99 };

  /* ---------------------------------------------------------------- 価格 */
  // つまみの目盛りは税込、送信値は税抜。Shopify の登録価格が税抜なので検索条件は税抜で渡す。
  // 丸め方は順方向（snippets/maruni-tax-included-amount.liquid）と一緒に直す。
  class MaruniPriceRange extends HTMLElement {
    connectedCallback() {
      this.minSlider = this.querySelector('[data-maruni-price-slider="min"]');
      this.maxSlider = this.querySelector('[data-maruni-price-slider="max"]');
      if (!this.minSlider || !this.maxSlider) return;

      this.currencySymbol = this.dataset.currencySymbol || '';
      this.fill = this.querySelector('[data-maruni-price-fill]');
      this.minField = this.querySelector('[data-maruni-price-field="min"]');
      this.maxField = this.querySelector('[data-maruni-price-field="max"]');
      this.readoutMin = this.querySelector('[data-maruni-price-readout="min"]');
      this.readoutMax = this.querySelector('[data-maruni-price-readout="max"]');

      this.rangeMin = Number(this.minSlider.min);
      this.rangeMax = Number(this.minSlider.max);
      // input の step は 1 に固定してある（Liquid 側のコメント参照）。ブラウザが値を丸めないので、
      // 「表示」「送信値」「実際に描画されている検索結果」が食い違わない。
      // キーボードの 1 押しだけは粗く動かしたいので、そのぶんは自前で処理する。
      this.coarseStep = Number(this.dataset.coarseStep) || 1;

      // 税込 = floor((税抜 × multiplier + roundOffset) / 100)。端数処理はテーマ設定で切り替わるので、
      // 切り捨て除算に足し込むオフセットとして表現する（Liquid 側の case と同じ対応）。
      const taxRate = Number(this.dataset.taxRate);
      this.taxMultiplier = 100 + (Number.isFinite(taxRate) ? taxRate : 10);
      this.taxRoundOffset = TAX_ROUND_OFFSET[this.dataset.taxRounding] || 0;

      if (!this.boundOnInput) {
        this.boundOnInput = this.onSliderInput.bind(this);
        this.boundOnKeyDown = this.onKeyDown.bind(this);
        this.minSlider.addEventListener('input', this.boundOnInput);
        this.maxSlider.addEventListener('input', this.boundOnInput);
        this.minSlider.addEventListener('keydown', this.boundOnKeyDown);
        this.maxSlider.addEventListener('keydown', this.boundOnKeyDown);
      }

      this.render();
    }

    // 矢印キーは coarseStep で動かす（step=1 のままだと 1 円ずつしか動かない）
    onKeyDown(event) {
      const decrease = event.key === 'ArrowLeft' || event.key === 'ArrowDown';
      const increase = event.key === 'ArrowRight' || event.key === 'ArrowUp';
      if (!decrease && !increase) return;
      if (this.coarseStep <= 1) return;

      event.preventDefault();
      const slider = event.currentTarget;
      const delta = increase ? this.coarseStep : -this.coarseStep;
      const next = Math.min(this.rangeMax, Math.max(this.rangeMin, Number(slider.value) + delta));
      slider.value = next;
      this.onSliderInput({ currentTarget: slider });
    }

    onSliderInput(event) {
      let minValue = Number(this.minSlider.value);
      let maxValue = Number(this.maxSlider.value);

      if (event.currentTarget.dataset.maruniPriceSlider === 'min' && minValue > maxValue) {
        minValue = maxValue;
        this.minSlider.value = minValue;
      }
      if (event.currentTarget.dataset.maruniPriceSlider === 'max' && maxValue < minValue) {
        maxValue = minValue;
        this.maxSlider.value = maxValue;
      }

      this.render();
      this.syncFields(minValue, maxValue);
      this.dispatchInput();
    }

    render() {
      const minValue = Number(this.minSlider.value);
      const maxValue = Number(this.maxSlider.value);
      const span = this.rangeMax - this.rangeMin || 1;

      if (this.fill) {
        this.fill.style.insetInlineStart = ((minValue - this.rangeMin) / span) * 100 + '%';
        this.fill.style.insetInlineEnd = 100 - ((maxValue - this.rangeMin) / span) * 100 + '%';
      }
      if (this.readoutMin) this.readoutMin.textContent = this.format(minValue);
      if (this.readoutMax) this.readoutMax.textContent = this.format(maxValue);

      this.minSlider.setAttribute('aria-valuetext', this.format(minValue));
      this.maxSlider.setAttribute('aria-valuetext', this.format(maxValue));

      // つまみが重なると、後から描かれる max 側がポインタを全部拾い、もう一方を掴めなくなる。
      // 重なったときは「動かせる余地が広いほう」を前面に出す（上半分なら min を下げられるように、
      // 下半分なら max を上げられるように）。右端で重なる場合も上半分なので min が前に出る。
      const overlapping = minValue >= maxValue;
      const midpoint = this.rangeMin + (this.rangeMax - this.rangeMin) / 2;
      this.minSlider.style.zIndex = overlapping && minValue >= midpoint ? '5' : '';
    }

    // 税込 L 以上になる最小の税抜（下限 gte 用）。
    // floor((P × m + c) / 100) >= L  ⟺  P >= (100L − c) / m
    taxExcludedForMin(included) {
      const numerator = included * 100 - this.taxRoundOffset;
      if (numerator <= 0) return 0;
      return Math.ceil(numerator / this.taxMultiplier);
    }

    // 税込 M 以下になる最大の税抜（上限 lte 用）。
    // floor((P × m + c) / 100) <= M  ⟺  P <= (100(M + 1) − 1 − c) / m
    taxExcludedForMax(included) {
      const numerator = (included + 1) * 100 - 1 - this.taxRoundOffset;
      if (numerator <= 0) return 0;
      return Math.floor(numerator / this.taxMultiplier);
    }

    // 両端（＝絞り込みなし）のときは value を空にして URL にパラメータを残さない。
    // 引数は税込、Shopify へ渡すのは税抜なので、ここで逆換算してから入れる。
    // 「以上 / 以下」で逆像の端が違うため、min と max で別の式を使う。
    syncFields(minValue, maxValue) {
      if (this.minField) {
        this.minField.value = minValue > this.rangeMin ? String(this.taxExcludedForMin(minValue)) : '';
      }
      if (this.maxField) {
        this.maxField.value = maxValue < this.rangeMax ? String(this.taxExcludedForMax(maxValue)) : '';
      }
    }

    dispatchInput() {
      const target = this.minField || this.maxField || this.minSlider;
      target.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // つまみの値はすでに税込なので、ここでは桁区切りと通貨記号を付けるだけ
    format(value) {
      return this.currencySymbol + Number(value).toLocaleString();
    }
  }

  if (!customElements.get('maruni-price-range')) {
    customElements.define('maruni-price-range', MaruniPriceRange);
  }

  /* ------------------------------------------------- SP バー / 並び替え */
  function initSearchChrome(root) {
    const sortModal = root.querySelector('[data-maruni-sort-modal]');
    const sortBackdrop = root.querySelector('[data-maruni-sort-backdrop]');

    const filterDetails = root.querySelector('.mobile-facets__wrapper details');
    let filterOpener = null;

    const openFilters = function () {
      // Rise の SP ドロワーは menu-drawer（global.js）が summary のクリックで開く。
      // フォーカストラップや body のスクロールロックもそこが持つので、まずクリックを通す。
      if (!filterDetails) return;
      const summary = filterDetails.querySelector('summary');
      if (summary) summary.click();
      // <details> の既定動作（open のトグル）が走らなかった場合の保険。
      // menu-drawer 側のハンドラはクリックで必ず走るので、ここでは open だけ補う。
      if (!filterDetails.open) filterDetails.open = true;
    };

    // 後始末（スクロールロック・フォーカストラップの解除）は menu-drawer に任せる。
    // `open` も自分で外さない。menu-drawer の `closeAnimation` が 400ms 後に外すので、
    // 先に外すと閉じるフェード（`:not(.menu-opening)` の opacity）が飛ぶ。
    const closeFilters = function () {
      if (!filterDetails) return;
      const summary = filterDetails.querySelector('summary');
      if (!summary) return;
      summary.click();
      // menu-drawer が `aria-expanded` を戻すのは「キーボードで閉じたとき」だけ
      // （`closeMenuDrawer` の `event instanceof KeyboardEvent`）。合成クリックなので補う。
      summary.setAttribute('aria-expanded', 'false');
    };

    // トリガーを SP バーへ移したぶん、フォーカスの面倒はこちらで見る。
    // menu-drawer は「クリックされた summary」を開いた要素として扱うが、その summary は
    // 視覚的に潰してあり tabindex="-1" なので、そのままだと閉じたときにフォーカスが消える。
    if (filterDetails) {
      // menu-drawer は群の summary のクリックでサブメニューを横へスライドさせる。そのハンドラは
      // summary へ直接（再描画のたび重ねて）付くので、上位の capture で止めるのが唯一の接点。
      // 既定動作は止めないので details は開閉する。
      filterDetails.addEventListener(
        'click',
        function (event) {
          const summary = event.target.closest('summary.mobile-facets__summary');
          if (!summary) return;
          event.stopImmediatePropagation();
          // ロック群（テイスト）は閉じる手段を出さないので既定動作ごと止める。上の
          // stopImmediatePropagation が summary 自身のリスナ（PC サイドバーが使っている
          // inline `onclick="event.preventDefault()"` を含む）ごと止めてしまうため、ここで見る。
          const group = summary.parentElement;
          if (group && group.hasAttribute('data-maruni-locked')) event.preventDefault();
        },
        true
      );

      // 「さらに表示」の展開を再描画後に復元する（詳細は maruni-facet-group.liquid）
      const isShowingMore = function (root) {
        return Boolean(root.querySelector('show-more-button .label-show-more.hidden'));
      };

      const restoreShowMore = function (group) {
        if (isShowingMore(group) || !group.querySelector('.show-more-item.hidden')) return;
        const showMore = group.querySelector('show-more-button');
        const button = showMore && showMore.querySelector('.button-show-more');
        if (!button || typeof showMore.expandShowMore !== 'function') return;
        showMore.expandShowMore({ target: button });
      };

      // menu-drawer が全 button に details を閉じるハンドラを張るので capture で止める（詳細は maruni-facet-group.liquid）
      filterDetails.addEventListener(
        'click',
        function (event) {
          const button = event.target.closest('.button-show-more');
          if (!button) return;
          event.stopImmediatePropagation();

          const showMore = button.closest('show-more-button');
          if (!showMore || typeof showMore.expandShowMore !== 'function') return;
          showMore.expandShowMore(event);

          const list = button.closest('.parent-display');
          const revealed = list && list.querySelector('.show-more-item');
          if (!revealed || revealed.classList.contains('hidden')) return;
          const input = revealed.querySelector('input');
          if (input) input.focus();
        },
        true
      );

      // ESC はドロワー全体を閉じる。menu-drawer の `onKeyUp` は入れ子の `<details open>` を
      // サブメニューとみなして「その群だけ」閉じ、ロック群がそれで閉じると開き直せなくなるので、
      // ここも capture 段階で止める。
      filterDetails.addEventListener(
        'keyup',
        function (event) {
          if (event.key !== 'Escape') return;
          event.stopImmediatePropagation();
          // ドロワー自身の summary。クリックで menu-drawer が閉じ、フォーカスも戻る
          const summary = filterDetails.querySelector('summary');
          if (!summary) return;
          summary.click();
          // menu-drawer が `aria-expanded` を戻すのは「キーボードで閉じたとき」だけ
          // （`closeMenuDrawer` の `event instanceof KeyboardEvent`）。ここは合成クリックで
          // 閉じているのでその分岐に入らない。開いたままだと読み上げへ嘘を伝えるので自分で戻す。
          summary.setAttribute('aria-expanded', 'false');
        },
        true
      );

      // チェックのたびに facets.js が群の中身を丸ごと差し替え、押したチェックボックスごと
      // フォーカスが消えるので、見えている群見出しで受ける。差し替えは入力から 800ms 遅れて
      // 来るため、変更の時点で動かすと「1 つ選ぶたびに群の先頭へ飛ぶ」。
      let lastChangedGroup = null;
      filterDetails.addEventListener('change', function (event) {
        lastChangedGroup = event.target.closest('.mobile-facets__details');
      });

      new MutationObserver(function (records) {
        // 外された古い値リストに「展開済み」の印が残っていたら、その群だけ戻す
        records.forEach(function (record) {
          Array.prototype.forEach.call(record.removedNodes, function (node) {
            if (node.nodeType !== 1 || !node.classList.contains('mobile-facets__list')) return;
            if (!isShowingMore(node)) return;
            const group = record.target.closest('.mobile-facets__details');
            if (group) restoreShowMore(group);
          });
        });

        if (!lastChangedGroup || !filterDetails.open) return;
        // フォーカスが生きているなら触らない（差し替え前／利用者が自分で移したあと）
        if (document.activeElement && document.activeElement !== document.body) return;
        const summary = lastChangedGroup.querySelector('summary');
        if (summary) summary.focus();
        lastChangedGroup = null;
      }).observe(filterDetails, { childList: true, subtree: true });

      // その群に条件が入っているか（チェック済み、または価格の送信値が空でない）。
      const hasActiveInput = function (group) {
        if (group.querySelector('input:checked')) return true;
        return Array.prototype.some.call(group.querySelectorAll('[data-maruni-price-field]'), function (field) {
          return field.value !== '';
        });
      };

      filterDetails.addEventListener('toggle', function () {
        if (filterDetails.open) {
          // menu-drawer は閉じるときに配下の details から open を全部外すので、開き直したときは
          // 既定で開く群と条件が入っている群をここで開き直す。`data-maruni-open-default` は
          // ページを描いた時点の印なので、あとから選んだぶんは現在の入力で見る。
          filterDetails.querySelectorAll('.mobile-facets__details').forEach(function (group) {
            if (group.hasAttribute('data-maruni-open-default') || hasActiveInput(group)) group.open = true;
          });
          // フォーカスはパネル自体（tabindex="-1"）で受け、以降は Tab で中を辿ってもらう
          // （先頭は右上の ✕ = `[data-maruni-filters-close]`）。
          const panel = filterDetails.querySelector('[data-maruni-drawer-panel]');
          if (panel) panel.focus();
        } else if (filterOpener) {
          // 画面幅が変わってバーが消えているときに、見えない要素へフォーカスを戻さない
          if (filterOpener.offsetParent !== null) filterOpener.focus();
          filterOpener = null;
        }
      });
    }

    let sortOpener = null;

    const sortOptions = root.querySelectorAll('[data-maruni-sort-value]');

    // モーダルは facets.js の再描画対象ではないので、選択状態が古いまま残る
    // （PC の <select> で並び替えてから SP 幅にした / 履歴を戻った 等）。
    // 正は常にドロワー内の <select> なので、開くたびにそこから取り直す。
    const syncSortSelection = function () {
      const select = document.getElementById('SortBy-mobile');
      if (!select) return;
      sortOptions.forEach(function (option) {
        option.setAttribute('aria-current', option.dataset.maruniSortValue === select.value ? 'true' : 'false');
      });
    };

    const setSortModal = function (open) {
      if (!sortModal) return;
      if (open) syncSortSelection();
      sortModal.classList.toggle('is-open', open);
      if (sortBackdrop) sortBackdrop.classList.toggle('is-open', open);
      sortModal.setAttribute('aria-hidden', open ? 'false' : 'true');
      // 背面のスクロールを止める。ドロワー（menu-drawer）が使うのと同じクラスに揃える
      document.body.classList.toggle('overflow-hidden-mobile', open);

      // aria-modal のダイアログなので、開いているあいだはフォーカスを中に閉じ込める。
      // トラップはテーマ共通の実装（global.js）を使う。読み込み順の都合で未定義なら何もしない。
      if (open) {
        if (typeof trapFocus === 'function') {
          trapFocus(sortModal, sortModal.querySelector('[data-maruni-sort-value]'));
        }
      } else if (typeof removeTrapFocus === 'function') {
        // 見えなくなった要素へフォーカスを戻さない（画面幅が変わって SP バーが消えた場合）
        removeTrapFocus(sortOpener && sortOpener.offsetParent !== null ? sortOpener : null);
        sortOpener = null;
      }
    };

    root.querySelectorAll('[data-maruni-open]').forEach(function (button) {
      button.addEventListener('click', function () {
        if (button.dataset.maruniOpen === 'filters') {
          filterOpener = button;
          openFilters();
        }
        if (button.dataset.maruniOpen === 'sort') {
          sortOpener = button;
          setSortModal(true);
        }
      });
    });

    if (sortBackdrop) sortBackdrop.addEventListener('click', () => setSortModal(false));
    root.querySelectorAll('[data-maruni-sort-close]').forEach(function (button) {
      button.addEventListener('click', () => setSortModal(false));
    });
    // 見出し行は facets.js の再描画対象外（`renderAdditionalElements` が触るのは
    // `.mobile-facets__open` / `.mobile-facets__count` / `.sorting` だけ）なので張り替え不要。
    root.querySelectorAll('[data-maruni-filters-close]').forEach(function (button) {
      button.addEventListener('click', closeFilters);
    });
    // 開いているときだけ閉じる。無条件に閉じると、ドロワー側が張っている
    // フォーカストラップ（global.js の共有ハンドラ）まで外してしまう。
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      if (sortModal && sortModal.classList.contains('is-open')) setSortModal(false);
    });

    // SP 用のオーバーレイ（ドロワー / 並び替えモーダル）は、CSS では PC 幅で隠れるだけで
    // 開いた状態とフォーカストラップが残る。見えない中身にフォーカスが入り、SP 幅へ戻すと
    // 開いたままのものが復活するので、breakpoint をまたいだら明示的に閉じる。
    const mobileQuery = window.matchMedia('(max-width: 749px)');
    const closeMobileOverlays = function () {
      if (sortModal && sortModal.classList.contains('is-open')) setSortModal(false);
      if (filterDetails && filterDetails.open) {
        // menu-drawer 側の後始末（body のスクロールロック解除・トラップ解除）に乗せる
        const summary = filterDetails.querySelector('summary');
        if (summary) summary.click();
        if (filterDetails.open) filterDetails.open = false;
      }
    };
    mobileQuery.addEventListener('change', function () {
      if (!mobileQuery.matches) closeMobileOverlays();
    });

    // IntersectionObserver は使わない。このページはバーの上に「テイストから探す」が入るため、
    // 端末によっては sentinel が初回から画面外（下）にあり、交差状態が変わらないまま上へ抜けて
    // コールバックが一度も発火しない。位置を直接見るため、スクロールを rAF で間引いて監視する。
    const spBar = root.querySelector('[data-maruni-spbar]');
    const spSentinel = root.querySelector('[data-maruni-spbar-sentinel]');
    const spSpacer = root.querySelector('[data-maruni-spbar-spacer]');
    if (spBar && spSentinel) {
      let queued = false;
      const applyDockState = function () {
        queued = false;
        const docked = spSentinel.getBoundingClientRect().top < 0;
        spBar.classList.toggle('is-docked', docked);
        if (spSpacer) spSpacer.classList.toggle('is-on', docked);
      };
      const requestDockState = function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(applyDockState);
      };
      window.addEventListener('scroll', requestDockState, { passive: true });
      window.addEventListener('resize', requestDockState, { passive: true });
      applyDockState();
    }

    sortOptions.forEach(function (option) {
      option.addEventListener('click', function () {
        // 並び替えの送信は Rise のドロワー内 <select> に任せる（facets.js が form の
        // input を拾って再検索する）。select が無い＝並び替え非表示なので何もしない。
        const select = document.getElementById('SortBy-mobile');
        setSortModal(false);
        if (!select) return;

        select.value = option.dataset.maruniSortValue;
        syncSortSelection();
        select.dispatchEvent(new Event('input', { bubbles: true }));
      });
    });
  }

  /* --------------------------------- 開いた絞り込み群を視界に入れる */
  // 起点は `toggle` ではなく summary のクリックにする。ドロワーを開き直すたびに既定オープンの
  // 群へ `open` が入り直すので、`toggle` で受けると触っていない群のぶんまでスクロールが走る。
  function initFacetReveal(root) {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');

    root.addEventListener(
      'click',
      function (event) {
        const summary = event.target.closest('summary.facets__summary, summary.mobile-facets__summary');
        if (!summary) return;
        const group = summary.parentElement;
        if (!group) return;
        // 「開いているか」だけでは足りず、閉から開へ変わったかで見る。ロック群（テイスト）は
        // クリックを preventDefault されて開いたままなので、開閉の結果だけ見ると見出しを押すたびに動く。
        const wasOpen = group.open;

        // 既定動作（`open` の反転）はリスナを抜けたあとに走るので、開いた状態の寸法は次フレームで見る
        requestAnimationFrame(function () {
          if (wasOpen || !group.open) return;
          // SP の確定バー（`.mobile-facets__footer`）はパネルのスクロールポートへ
          // `position: sticky; bottom: 0` で貼り付くが、`scrollIntoView` はそれを障害物として
          // 数えない。下端にぴたりと寄せると最後の選択肢がバーの裏に入るので、同じ高さだけ逃がす。
          const panel = group.closest('.mobile-facets__inner');
          const applyBar = panel && panel.querySelector('.mobile-facets__footer');
          group.style.scrollMarginBottom = applyBar ? applyBar.offsetHeight + 'px' : '';
          // `block` を `'start'` や `'center'` にしてはいけない。すでに全部見えている群でも
          // 必ず寄せに行くので、上のほうの群を開いただけでスクロール位置が動く。
          group.scrollIntoView({ behavior: motion.matches ? 'auto' : 'smooth', block: 'nearest' });
        });
      },
      // ドロワー内の summary クリックは `initSearchChrome` が capture で
      // `stopImmediatePropagation()` するので、その手前にある root の capture で受ける
      true
    );
  }

  /* ------------------------------------- 一覧の先頭へ戻す */
  // 着地点を自前に持たない。アンカー遷移・ページ送りと同じ箱に寄せると、sticky ヘッダーの下に
  // 潜らない `scroll-margin-top`（`assets/maruni-search.css`）をそのまま使える。
  function scrollToResults() {
    const results = document.querySelector('.mrn-search__results');
    if (!results) return;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    results.scrollIntoView({ behavior: motion.matches ? 'auto' : 'smooth', block: 'start' });
  }

  // 入力・クリックの時点でスクロールしない。facets.js は再描画の中で触った群へ `focus()` する
  // ので、先に動かすとそのフォーカス移動に視点を奪われる。`#ProductGridContainer` の差し替えは
  // focus より後に来るため、その監視で受ければ上書きされない。
  function initResultsAnchor() {
    const grid = document.getElementById('ProductGridContainer');
    if (!grid) return;
    // 監視は箱そのものに張る。facets.js が入れ替えるのは innerHTML ＝箱は残るので、
    // 再描画のたびに張り替える必要はない。
    new MutationObserver(scrollToResults).observe(grid, { childList: true });
  }

  /* ------------------------------------- テイスト導線を遷移なしにする */
  // フィルター付き URL への素のリンクを遷移させず、チェック操作で facets.js に再描画させる（URL
  // 更新・履歴も facets.js 任せ）。href は消さない。JS 無効時・値がパネルに無いときは遷移に任せる。
  function initFacetLinks() {
    const form = document.getElementById('FacetFiltersForm');
    if (!form) return;

    // リンクの href に載っている filter パラメータを、サイドバーのチェック状態へ写す。
    // 表現しきれない（該当する入力が無い）ときは false を返し、呼び出し側が遷移に委ねる。
    const applyFacetsFromHref = function (href) {
      let params;
      try {
        params = new URL(href, window.location.href).searchParams;
      } catch (error) {
        return false;
      }

      const wanted = [];
      params.forEach(function (value, key) {
        if (key.indexOf('filter.') === 0) wanted.push({ key: key, value: value });
      });
      if (!wanted.length) return false;

      const boxes = Array.from(form.querySelectorAll('input[type="checkbox"][name^="filter."]'));
      const targets = wanted.map(function (entry) {
        return boxes.find(function (box) {
          return box.name === entry.key && box.value === entry.value;
        });
      });
      if (targets.some(function (box) { return !box; })) return false;

      // リンクは「絞り込みを置き換える」導線（URL を毎回まっさらに組み立てている）なので、
      // 既存の選択はすべて落としてから目的の値だけを入れる。価格の hidden も空に戻す。
      boxes.forEach(function (box) { box.checked = false; });
      form.querySelectorAll('input[type="hidden"][name^="filter.v.price"]').forEach(function (input) {
        input.value = '';
      });
      targets.forEach(function (box) {
        // 件数 0 の値は disabled で描画され、そのままだと FormData から落ちて絞り込みに
        // 載らない。リンクで遷移したときは 0 件でもその絞り込みが効くので、挙動を合わせるため
        // ここで送る値だけ disabled を外す。再描画でサーバー側の状態に戻るので残らない。
        box.disabled = false;
        const label = box.closest('.facets__label');
        if (label) label.classList.remove('disabled');
        box.checked = true;
      });

      // 発火元はフォーム自身にする。チェックボックスから投げると facets.js が
      // 「その群だけ件数を差し替えてサマリへフォーカスを戻す」経路に入り、
      // 結果欄へスクロールしたそばからサイドバーへ視点が飛ぶ。
      form.dispatchEvent(new Event('input', { bubbles: true }));
      return true;
    };

    document.addEventListener('click', function (event) {
      const link = event.target.closest('[data-maruni-facet-link]');
      if (!link) return;
      // 新しいタブで開く操作・中クリックは横取りしない
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      if (!applyFacetsFromHref(link.href)) return;
      event.preventDefault();

      // 再描画待ち（initResultsAnchor）に任せない。facets.js の debounce で 800ms 遅れ、
      // カードを押しても何も起きない間が空く。
      scrollToResults();
    });
  }

  document.querySelectorAll('[data-maruni-search]').forEach(function (root) {
    initSearchChrome(root);
    initFacetReveal(root);
  });
  initFacetLinks();
  initResultsAnchor();
})();
